import React, {useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, useBreakpointValue, useSlotRecipe} from '@chakra-ui/react3'

import Image from './image'
import {
    hasValidImageSrc,
    isFullVideo,
    isDarkenOff as isDarkenOffUtil,
    normalizeDarken
} from '../../utils/utils'
import ModuleContainer from '../../components/module-container'
import AspectRatio from '../../components/cms/aspect-ratio'
import MediaAnimation from './animation'
import Asset from '../../components/cms/asset'
import {getMediaAssetForViewport} from '../../utils/media'
import {mediaSlotRecipe, MODULE_CONTAINER_LAYOUT_MAP} from './index.recipe'

export {getMediaAssetForViewport} from '../../utils/media'

/**
 * MediaModule component renders media content (images or videos) with optional animation and darkening overlay.
 * Supports both image and video assets with customizable display options.
 *
 * @param {Object} props - The component props
 * @param {string} props.moduleId - Unique identifier for the module
 * @param {string} props.moduleType - Type of the module for categorization
 * @param {string} props.pageId - Identifier of the page containing this module
 * @param {string} [props.anchor] - Optional anchor ID for navigation linking
 * @param {Object} [props.videoConfig] - Configuration object for video playback settings
 * @param {boolean} [props.withAnimation] - Whether to wrap content with animation component
 * @param {string} [props.duration] - Duration string for video content display
 * @param {string} [props.videoName] - Name/title to display for video content
 * @param {Object} [props.asset] - Media asset Portrait (4:5) object containing src, type, and other media properties
 * @param {Object} [props.assetLandscape] - Media asset Landscape (16:9) object containing src, type, and other media properties
 * @param {string} [props.darken] - Percentage string for overlay darkening effect (e.g., "20%")
 * @param {'full-width'|'inline'} [props.layout='full-width'] - Layout variant determining whether media spans full width or sits inline in content container
 * @returns {JSX.Element|null} Rendered media module or null if asset is invalid
 */
const MediaModule = React.forwardRef((props, ref) => {
    const {
        moduleId,
        moduleType,
        pageId,
        anchor,
        withAnimation,
        asset,
        assetLandscape,
        darken: darkenPercent,
        layout = 'full-width',
        variant = 'landscape'
    } = props
    const isDesktop = useBreakpointValue({base: false, s: true}) || false
    const selectedAsset = getMediaAssetForViewport({
        asset,
        assetLandscape,
        variant,
        isDesktop
    })
    const isDarkenOff = isDarkenOffUtil(darkenPercent)
    const normalizedDarken = normalizeDarken(darkenPercent)
    const containerProps =
        MODULE_CONTAINER_LAYOUT_MAP[layout] || MODULE_CONTAINER_LAYOUT_MAP['full-width']
    const recipe = useSlotRecipe({recipe: mediaSlotRecipe})
    const styles = useMemo(
        () => recipe({layout, darken: normalizedDarken}),
        [recipe, layout, normalizedDarken]
    )
    const videoRef = useRef(null)
    const isFullVideoMode = useMemo(() => isFullVideo(selectedAsset), [selectedAsset])

    if (!selectedAsset) {
        console.warn(`Asset not supported`)
        return null
    }

    const content = hasValidImageSrc(selectedAsset) ? (
        <Box css={styles.mediaWrapper}>
            <AspectRatio ratio={{base: variant === 'portrait' ? 4 / 5 : 16 / 9, s: 16 / 9}}>
                <Image asset={selectedAsset} />
            </AspectRatio>
        </Box>
    ) : (
        <Box css={styles.mediaWrapper}>
            <AspectRatio ratio={{base: variant === 'portrait' ? 4 / 5 : 16 / 9, s: 16 / 9}}>
                <Asset
                    variant={props.videoName ? 'videoWithInfo' : 'default'}
                    asset={{
                        ...selectedAsset,
                        autoplay: true,
                        videoName: props.videoName,
                        duration: props.duration
                    }}
                    videoRef={videoRef}
                    objectFit="cover"
                    darken={normalizedDarken}
                />
            </AspectRatio>
            {/* Module-level darken overlay — not shown in full video mode or when darken is off.
                In full video mode, the Asset component handles darken internally. */}
            {!isFullVideoMode && !isDarkenOff && <Box css={styles.darkenLayer} />}
        </Box>
    )

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            css={styles.container}
            spacing="none"
            modulePadding={containerProps.modulePadding}
            layerStyle={containerProps.layerStyle}
            ref={ref}
        >
            {withAnimation ? <MediaAnimation layout={layout}>{content}</MediaAnimation> : content}
        </ModuleContainer>
    )
})

if (process.env.NODE_ENV !== 'production') {
    MediaModule.displayName = 'MediaModule'
}

MediaModule.propTypes = {
    moduleId: PropTypes.string.isRequired,
    moduleType: PropTypes.string.isRequired,
    pageId: PropTypes.string.isRequired,
    anchor: PropTypes.string,
    videoConfig: PropTypes.object,
    withAnimation: PropTypes.bool,
    duration: PropTypes.string,
    videoName: PropTypes.string,
    asset: PropTypes.object,
    assetLandscape: PropTypes.object,
    variant: PropTypes.oneOf(['landscape', 'portrait']),
    darken: PropTypes.string,
    layout: PropTypes.oneOf(['full-width', 'inline'])
}

export default MediaModule
